import { useMemo, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CUISINE_GROUPS } from "@/lib/chef-taxonomy";

type BaseProps = {
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  /** Limite di selezione (solo multi). 0 = illimitato. */
  max?: number;
  /** "Senza categoria" item per la modalità single. */
  allowAny?: boolean;
  ariaLabel?: string;
};

export function CuisineComboboxMulti({
  value,
  onChange,
  placeholder = "Scegli categorie",
  className,
  triggerClassName,
  max = 0,
  ariaLabel,
}: BaseProps & {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = new Set(value);

  // Mostriamo anche valori legacy non in catalogo: gruppo dedicato.
  const legacy = useMemo(() => {
    const all = new Set(CUISINE_GROUPS.flatMap((g) => g.items));
    return value.filter((v) => !all.has(v));
  }, [value]);

  const toggle = (item: string) => {
    if (selected.has(item)) {
      onChange(value.filter((v) => v !== item));
      return;
    }
    if (max > 0 && value.length >= max) return;
    onChange([...value, item]);
  };

  return (
    <div className={cn("space-y-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-label={ariaLabel ?? placeholder}
            aria-expanded={open}
            className={cn("w-full justify-between font-normal", triggerClassName)}
          >
            <span className="truncate text-left">
              {value.length === 0
                ? <span className="text-muted-foreground">{placeholder}</span>
                : `${value.length} selezionate${max > 0 ? ` / ${max}` : ""}`}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[260px] p-0" align="start">
          <Command
            filter={(itemValue, search) => {
              const v = itemValue.toLowerCase();
              const s = search.trim().toLowerCase();
              if (!s) return 1;
              return v.includes(s) ? 1 : 0;
            }}
          >
            <CommandInput placeholder="Cerca (es. sushi, veg, pizza)…" />
            <CommandList>
              <CommandEmpty>Nessuna categoria.</CommandEmpty>
              {legacy.length > 0 && (
                <CommandGroup heading="Altre">
                  {legacy.map((item) => (
                    <CommandItem key={item} value={item} onSelect={() => toggle(item)}>
                      <Check className={cn("mr-2 h-4 w-4", selected.has(item) ? "opacity-100 text-gold" : "opacity-0")} />
                      {item}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {CUISINE_GROUPS.map((g) => (
                <CommandGroup key={g.label} heading={g.label}>
                  {g.items.map((item) => {
                    const isSel = selected.has(item);
                    const disabled = !isSel && max > 0 && value.length >= max;
                    return (
                      <CommandItem
                        key={item}
                        value={item}
                        disabled={disabled}
                        onSelect={() => toggle(item)}
                      >
                        <Check className={cn("mr-2 h-4 w-4", isSel ? "opacity-100 text-gold" : "opacity-0")} />
                        <span className={cn(disabled && "text-muted-foreground")}>{item}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => onChange(value.filter((x) => x !== v))}
              className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs transition hover:bg-secondary/70"
              aria-label={`Rimuovi ${v}`}
            >
              {v} <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function CuisineComboboxSingle({
  value,
  onChange,
  placeholder = "Tipo di cucina",
  className,
  triggerClassName,
  allowAny = true,
  ariaLabel,
}: BaseProps & {
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          role="combobox"
          aria-label={ariaLabel ?? placeholder}
          aria-expanded={open}
          className={cn("w-full justify-between px-0 font-normal hover:bg-transparent", triggerClassName, className)}
        >
          <span className="truncate text-left text-sm">
            {value || <span className="text-muted-foreground">{placeholder}</span>}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[260px] p-0" align="start">
        <Command
          filter={(itemValue, search) => {
            const v = itemValue.toLowerCase();
            const s = search.trim().toLowerCase();
            if (!s) return 1;
            return v.includes(s) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Cerca (es. sushi, veg, pizza)…" />
          <CommandList>
            <CommandEmpty>Nessuna categoria.</CommandEmpty>
            {allowAny && (
              <CommandGroup>
                <CommandItem value="" onSelect={() => { onChange(""); setOpen(false); }}>
                  <Check className={cn("mr-2 h-4 w-4", value === "" ? "opacity-100 text-gold" : "opacity-0")} />
                  Tutte le cucine
                </CommandItem>
              </CommandGroup>
            )}
            {CUISINE_GROUPS.map((g) => (
              <CommandGroup key={g.label} heading={g.label}>
                {g.items.map((item) => (
                  <CommandItem
                    key={item}
                    value={item}
                    onSelect={() => { onChange(item); setOpen(false); }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", value === item ? "opacity-100 text-gold" : "opacity-0")} />
                    {item}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}